import React, { useState, useRef } from 'react';
import { FiUpload, FiFile, FiCheckCircle, FiXCircle, FiAlertCircle } from 'react-icons/fi';
import * as XLSX from 'xlsx';
import axios from 'axios';
import { ToastContainer, toast } from 'react-toastify';
import { Slide } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { BASE_URL, POST_MODULES } from '../../utils/config';

// Header ID to Header Name mapping
const HEADER_NAME_MAP = {
  1: 'Transactions',
  2: 'Masters',
  3: 'Reports',
  4: 'Payments'
};

// Full screen loader with progress
const ProgressLoader = ({ progress, current, total, status }) => (
  <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black bg-opacity-70 backdrop-blur-sm">
    <div className="bg-white rounded-2xl shadow-2xl p-8 w-full max-w-md">
      <div className="text-center mb-6">
        <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-indigo-100 mb-4">
          <FiUpload className="text-indigo-600" size={32} />
        </div>
        <h3 className="text-xl font-bold text-gray-800 mb-2">Uploading Modules</h3>
        <p className="text-gray-600 text-sm">{status}</p>
      </div>

      {/* Progress Bar */}
      <div className="mb-4">
        <div className="flex justify-between text-sm text-gray-600 mb-2">
          <span>Progress: {current} / {total}</span>
          <span>{progress}%</span>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-4 overflow-hidden">
          <div
            className="bg-gradient-to-r from-indigo-600 to-purple-600 h-4 rounded-full transition-all duration-300 ease-out"
            style={{ width: `${progress}%` }}
          />
        </div>
      </div>

      <div className="text-center text-sm text-gray-500">
        Please wait while modules are being uploaded...
      </div>
    </div>
  </div>
);

const AddAuthModules = () => {
  const [file, setFile] = useState(null);
  const [parsedData, setParsedData] = useState([]);
  const [groupedData, setGroupedData] = useState([]);
  const [parsing, setParsing] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [totalGroups, setTotalGroups] = useState(0);
  const [status, setStatus] = useState('');
  const [uploadResults, setUploadResults] = useState({ success: 0, failed: 0, errors: [] });
  const fileInputRef = useRef(null);

  const token = localStorage.getItem('accessToken');

  // Helper function to convert Excel value to proper type
  const convertValue = (value) => {
    if (value === null || value === undefined || value === 'NULL' || value === '') {
      return null;
    }
    if (typeof value === 'string' && value.trim() === '') {
      return null;
    }
    return value;
  };

  const handleFileChange = async (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      if (selectedFile.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ||
          selectedFile.type === 'application/vnd.ms-excel' ||
          selectedFile.name.endsWith('.xlsx') ||
          selectedFile.name.endsWith('.xls')) {
        setFile(selectedFile);
        setParsing(true);
        setUploadResults({ success: 0, failed: 0, errors: [] });
        
        try {
          // Parse Excel file immediately
          const data = await parseExcelFile(selectedFile);
          if (data && data.length > 0) {
            setParsedData(data);
            // Group data by headerID and appName
            const grouped = groupModulesByHeaderAndApp(data);
            setGroupedData(grouped);
            toast.success(`Successfully read ${data.length} modules from file`, { autoClose: 2000 });
          } else {
            toast.error('Excel file is empty or invalid', { autoClose: 2000 });
            setFile(null);
            setParsedData([]);
            setGroupedData([]);
          }
        } catch (error) {
          console.error('Error parsing file:', error);
          toast.error('Failed to parse Excel file: ' + (error.message || 'Unknown error'), { autoClose: 2000 });
          setFile(null);
          setParsedData([]);
          setGroupedData([]);
        } finally {
          setParsing(false);
        }
      } else {
        toast.error('Please select a valid Excel file (.xlsx or .xls)', { autoClose: 2000 });
        setFile(null);
        setParsedData([]);
        setGroupedData([]);
      }
    }
  };

  const parseExcelFile = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const data = new Uint8Array(e.target.result);
          const workbook = XLSX.read(data, { type: 'array' });
          const firstSheetName = workbook.SheetNames[0];
          const worksheet = workbook.Sheets[firstSheetName];
          const jsonData = XLSX.utils.sheet_to_json(worksheet, { defval: null });
          resolve(jsonData);
        } catch (error) {
          reject(error);
        }
      };
      reader.onerror = reject;
      reader.readAsArrayBuffer(file);
    });
  };

  // Group modules by headerID and appName
  const groupModulesByHeaderAndApp = (data) => {
    const grouped = {};
    
    data.forEach((row) => {
      const headerID = parseInt(row.headerID || row['headerID'] || 0);
      const appName = convertValue(row.appname || row['appname'] || row.appName || row['appName']) || '';
      const moduleName = convertValue(row.modulename || row['modulename'] || row.moduleName || row['moduleName']) || '';
      
      if (headerID > 0 && headerID <= 4 && appName && moduleName) {
        const key = `${headerID}_${appName}`;
        
        if (!grouped[key]) {
          grouped[key] = {
            headerID: headerID,
            headerName: HEADER_NAME_MAP[headerID] || '',
            appName: appName,
            moduleNames: []
          };
        }
        
        if (!grouped[key].moduleNames.includes(moduleName)) {
          grouped[key].moduleNames.push(moduleName);
        }
      }
    });
    
    return Object.values(grouped);
  };

  const handleUpload = async () => {
    if (!file || groupedData.length === 0) {
      toast.error('Please select and parse an Excel file first', { autoClose: 2000 });
      return;
    }

    if (!token) {
      toast.error('Please authenticate first', { autoClose: 2000 });
      return;
    }

    setUploading(true);
    setProgress(0);
    setCurrentIndex(0);
    setUploadResults({ success: 0, failed: 0, errors: [] });

    try {
      setTotalGroups(groupedData.length);
      setStatus(`Uploading ${groupedData.length} module groups...`);

      let successCount = 0;
      let failedCount = 0;
      const errors = [];

      // Upload each group
      for (let i = 0; i < groupedData.length; i++) {
        const group = groupedData[i];
        setCurrentIndex(i + 1);
        setProgress(Math.round(((i + 1) / groupedData.length) * 100));
        setStatus(`Uploading group ${i + 1} of ${groupedData.length}: ${group.headerName} - ${group.appName}...`);

        try {
          const response = await axios.post(`${BASE_URL}${POST_MODULES}`, group, {
            headers: {
              'XApiKey': process.env.REACT_APP_API_KEY || '',
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            },
          });

          if (response.status === 200 || response.status === 201) {
            successCount++;
          } else {
            failedCount++;
            errors.push({
              index: i + 1,
              groupName: `${group.headerName} - ${group.appName}`,
              error: `API returned status ${response.status}`
            });
          }
        } catch (error) {
          failedCount++;
          const errorMessage = error.response?.data?.message || error.message || 'Unknown error';
          errors.push({
            index: i + 1,
            groupName: `${group.headerName} - ${group.appName}`,
            error: errorMessage
          });
        }
      }

      const results = {
        success: successCount,
        failed: failedCount,
        errors: errors
      };
      setUploadResults(results);
      
      if (failedCount === 0) {
        setStatus('Upload completed successfully!');
        toast.success(`Successfully uploaded all ${successCount} module groups!`, { autoClose: 2000 });
      } else {
        setStatus('Upload completed with some errors!');
        toast.warning(`Uploaded ${successCount} groups, ${failedCount} failed`, { autoClose: 3000 });
      }

      // Wait a bit before allowing user to continue
      await new Promise(resolve => setTimeout(resolve, 2000));
      setUploading(false);

    } catch (error) {
      console.error('Upload error:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Unknown error';
      const results = {
        success: 0,
        failed: groupedData.length,
        errors: [{
          index: 0,
          groupName: 'Bulk Upload',
          error: errorMessage
        }]
      };
      setUploadResults(results);
      setStatus('Upload failed!');
      toast.error('Failed to upload modules: ' + errorMessage, { autoClose: 3000 });
      setUploading(false);
    }
  };

  return (
    <div className="p-6 w-full">
      <ToastContainer
        position="top-center"
        autoClose={2000}
        limit={3}
        hideProgressBar={false}
        newestOnTop
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
        transition={Slide}
      />

      {uploading && (
        <ProgressLoader
          progress={progress}
          current={currentIndex}
          total={totalGroups}
          status={status}
        />
      )}

      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-800 flex items-center gap-3">
          <FiFile size={32} className="text-blue-600" />
          Add Auth Modules
        </h1>
        <p className="text-gray-500 mt-2">Upload Excel file to bulk add authentication modules</p>
      </div>

      <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
        <div className="space-y-6">
          {/* File Upload Area */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Select Excel File
            </label>
            <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center hover:border-blue-500 transition-colors">
              <input
                ref={fileInputRef}
                type="file"
                accept=".xlsx,.xls"
                onChange={handleFileChange}
                disabled={uploading}
                className="hidden"
                id="file-upload"
              />
              <label
                htmlFor="file-upload"
                className={`cursor-pointer flex flex-col items-center ${uploading ? 'opacity-50 cursor-not-allowed' : ''}`}
              >
                <FiUpload size={48} className="text-gray-400 mb-4" />
                <p className="text-gray-600 mb-2">
                  {file ? file.name : 'Click to upload or drag and drop'}
                </p>
                <p className="text-sm text-gray-400">
                  Excel files only (.xlsx, .xls)
                </p>
              </label>
            </div>
            {file && (
              <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded-lg flex items-center gap-3">
                <FiCheckCircle className="text-green-600" size={20} />
                <span className="text-green-700 font-medium">{file.name}</span>
                {parsing && (
                  <span className="text-blue-600 text-sm ml-2">(Parsing...)</span>
                )}
                {!parsing && parsedData.length > 0 && (
                  <span className="text-green-600 text-sm ml-2">
                    ({parsedData.length} modules found, {groupedData.length} groups)
                  </span>
                )}
                <button
                  onClick={() => {
                    setFile(null);
                    setParsedData([]);
                    setGroupedData([]);
                    setUploadResults({ success: 0, failed: 0, errors: [] });
                    if (fileInputRef.current) {
                      fileInputRef.current.value = '';
                    }
                  }}
                  className="ml-auto text-red-600 hover:text-red-700"
                  disabled={uploading || parsing}
                >
                  <FiXCircle size={20} />
                </button>
              </div>
            )}
          </div>

          {/* Parsed Data Table */}
          {!parsing && parsedData.length > 0 && (
            <div className="mt-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold text-gray-800">
                  Preview Data ({parsedData.length} modules)
                </h3>
                <span className="text-sm text-gray-500">
                  Review the data before submitting
                </span>
              </div>
              <div className="border border-gray-200 rounded-lg overflow-hidden">
                <div className="overflow-x-auto max-h-96 overflow-y-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50 sticky top-0">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">#</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Header ID</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Header Name</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Module Name</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">App Name</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {parsedData.map((row, index) => {
                        const headerID = parseInt(row.headerID || row['headerID'] || 0);
                        const moduleName = row.modulename || row['modulename'] || row.moduleName || row['moduleName'] || '';
                        const appName = row.appname || row['appname'] || row.appName || row['appName'] || '';
                        const headerName = HEADER_NAME_MAP[headerID] || 'Unknown';
                        
                        return (
                          <tr key={index} className="hover:bg-gray-50">
                            <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">{index + 1}</td>
                            <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">{headerID || '-'}</td>
                            <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900">{headerName}</td>
                            <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">{moduleName || '-'}</td>
                            <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">{appName || '-'}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {/* Grouped Data Preview */}
          {!parsing && groupedData.length > 0 && (
            <div className="mt-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold text-gray-800">
                  Grouped Data ({groupedData.length} groups)
                </h3>
                <span className="text-sm text-gray-500">
                  Modules grouped by Header ID and App Name
                </span>
              </div>
              <div className="border border-gray-200 rounded-lg overflow-hidden">
                <div className="overflow-x-auto max-h-96 overflow-y-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50 sticky top-0">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">#</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Header ID</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Header Name</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">App Name</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Module Names</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {groupedData.map((group, index) => (
                        <tr key={index} className="hover:bg-gray-50">
                          <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">{index + 1}</td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">{group.headerID}</td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900">{group.headerName}</td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">{group.appName}</td>
                          <td className="px-4 py-3 text-sm text-gray-500">
                            <div className="flex flex-wrap gap-1">
                              {group.moduleNames.map((name, idx) => (
                                <span key={idx} className="px-2 py-1 bg-blue-100 text-blue-800 rounded text-xs">
                                  {name}
                                </span>
                              ))}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {/* Excel Format Info */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <FiAlertCircle className="text-blue-600 mt-0.5" size={20} />
              <div>
                <h3 className="font-semibold text-blue-900 mb-2">Expected Excel Format</h3>
                <p className="text-sm text-blue-700 mb-2">
                  Your Excel file should contain the following columns:
                </p>
                <ul className="text-xs text-blue-600 space-y-1 list-disc list-inside">
                  <li><strong>Required Columns:</strong> headerID, modulename, appname</li>
                  <li><strong>Header ID Mapping:</strong>
                    <ul className="ml-4 mt-1 space-y-0.5">
                      <li>1 → Transactions</li>
                      <li>2 → Masters</li>
                      <li>3 → Reports</li>
                      <li>4 → Payments</li>
                    </ul>
                  </li>
                  <li><strong>Note:</strong> Modules will be automatically grouped by headerID and appName. Multiple modules with the same headerID and appName will be combined into a single API call.</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Upload Button */}
          {groupedData.length > 0 && (
            <div className="flex justify-end gap-4">
              <button
                onClick={() => {
                  setFile(null);
                  setParsedData([]);
                  setGroupedData([]);
                  setUploadResults({ success: 0, failed: 0, errors: [] });
                  if (fileInputRef.current) {
                    fileInputRef.current.value = '';
                  }
                }}
                disabled={uploading || parsing}
                className="px-6 py-3 bg-gray-200 text-gray-700 rounded-lg font-semibold hover:bg-gray-300 transition-all ${
                  (uploading || parsing) ? 'opacity-70 cursor-not-allowed' : ''
                }"
              >
                Clear
              </button>
              <button
                onClick={handleUpload}
                disabled={!file || uploading || parsing || groupedData.length === 0}
                className={`px-6 py-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg font-semibold hover:from-blue-700 hover:to-purple-700 transition-all transform hover:scale-[1.02] shadow-lg ${
                  (!file || uploading || parsing || groupedData.length === 0) ? 'opacity-70 cursor-not-allowed' : ''
                }`}
              >
                {uploading ? 'Uploading...' : `Submit ${groupedData.length} Groups`}
              </button>
            </div>
          )}

          {/* Upload Results */}
          {!uploading && uploadResults.success + uploadResults.failed > 0 && (
            <div className="mt-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
              <h3 className="font-semibold text-gray-800 mb-3">Upload Results</h3>
              <div className="grid grid-cols-2 gap-4 mb-4">
                <div className="p-3 bg-green-50 rounded-lg border border-green-200">
                  <p className="text-sm text-green-700">Successful</p>
                  <p className="text-2xl font-bold text-green-600">{uploadResults.success}</p>
                </div>
                <div className="p-3 bg-red-50 rounded-lg border border-red-200">
                  <p className="text-sm text-red-700">Failed</p>
                  <p className="text-2xl font-bold text-red-600">{uploadResults.failed}</p>
                </div>
              </div>
              {uploadResults.errors.length > 0 && (
                <div className="mt-4">
                  <p className="text-sm font-medium text-gray-700 mb-2">Errors:</p>
                  <div className="max-h-40 overflow-y-auto space-y-1">
                    {uploadResults.errors.slice(0, 10).map((error, idx) => (
                      <div key={idx} className="text-xs text-red-600 p-2 bg-red-50 rounded">
                        Group {error.index}: {error.groupName} - {error.error}
                      </div>
                    ))}
                    {uploadResults.errors.length > 10 && (
                      <p className="text-xs text-gray-500 mt-2">
                        ... and {uploadResults.errors.length - 10} more errors
                      </p>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AddAuthModules;
